import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  ButtonValid,
  CardV2 as Card,
  ConfirmModal,
  EmptyState,
  Form,
  Skeleton,
} from '@feathr/components';
import { errorMessage } from '@feathr/hooks';

import AdGroupCard from './AdGroupCard';
import type { IAdGroup, IAdGroupsController } from './AdStep.types';
import { AD_GRANT_LIMITS } from './AdStep.utils';

import * as styles from './AdStep.css';

interface IAdStepProps extends IAdGroupsController {
  /** The advertiser name Google shows above each ad's display URL, from the campaign. */
  businessName?: string;
  /**
   * An ad group to open for editing on arrival, set when the Review step's Edit button
   * sent the user here. Keyed by id rather than position, so an ad group deleted since the
   * request was made resolves to nothing instead of locking the step behind an index that
   * no card owns.
   */
  editAdGroupKey?: string | null;
  disabled?: boolean;
}

/**
 * Ad-building step for Google Ad Grants "Search" campaigns.
 *
 * Search folds the keyword and ad-text editing that Smart splits across separate steps
 * into this one, because Search introduces ad groups as the container both belong to.
 * The ad groups and their ads come from the API — see `useAdGroups`.
 *
 * @param props - {@link IAdStepProps}.
 */
function AdStep({
  adGroups,
  addAd,
  addAdGroup,
  isLoaded,
  loadError,
  removeAd,
  removeAdGroup,
  renameAdGroup,
  saveAdGroup,
  updateAd,
  updateAdGroup,
  validation,
  businessName,
  editAdGroupKey,
  disabled = false,
}: Readonly<IAdStepProps>): JSX.Element {
  const { t } = useTranslation();
  const [editingAdGroup, setEditingAdGroup] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const hasOpenedRequested = useRef(false);

  const firstInvalidIndex = validation.invalidAdGroupIndexes[0];

  /*
   * Opens the ad group the Review step asked for, once it is in the list — the ad groups
   * may still be loading on the first render. Fires once: without the guard, finishing the
   * edit and then adding another ad group would reopen it.
   */
  useEffect(() => {
    if (hasOpenedRequested.current || !editAdGroupKey) {
      return;
    }
    const index = adGroups.findIndex((adGroup) => adGroup.key === editAdGroupKey);
    if (index === -1) {
      return;
    }

    hasOpenedRequested.current = true;
    setEditingAdGroup(index);

    /*
     * The step is arrived at scrolled to the top, and an ad group below the fold would
     * open with nothing to show for it. Queried off this step's own root rather than
     * threading a ref through AdGroupCard and ExpandableSection to the Card that owns the
     * element.
     */
    rootRef.current
      ?.querySelector(`[data-name="ad-group-${index + 1}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [adGroups, editAdGroupKey]);

  function handleEdit(index: number): void {
    setEditingAdGroup(index);
  }

  async function handleDone(): Promise<void> {
    const index = editingAdGroup;
    // Close first: the save reports its own failures, and leaving the editor open on
    // one would strand the user in a card they can't get out of.
    setEditingAdGroup(null);
    if (index !== null) {
      await saveAdGroup(index);
    }
  }

  async function handleAdd(): Promise<void> {
    setIsBusy(true);
    // Only the create knows where the group landed, and whether it landed at all — a
    // failure leaves the list as it was, and opening an index it doesn't have would lock
    // every card with no Done button to escape with.
    const newIndex = await addAdGroup();
    setIsBusy(false);
    if (newIndex !== null) {
      setEditingAdGroup(newIndex);
    }
  }

  function handleRequestDelete(index: number): void {
    setPendingDelete(index);
  }

  function handleCancelDelete(): void {
    setPendingDelete(null);
  }

  async function handleConfirmDelete(): Promise<void> {
    if (pendingDelete === null) {
      return;
    }
    const index = pendingDelete;
    setEditingAdGroup(null);
    setPendingDelete(null);
    setIsBusy(true);
    await removeAdGroup(index);
    setIsBusy(false);
  }

  const addAdGroupErrors: string[] = [];
  if (editingAdGroup !== null) {
    addAdGroupErrors.push(t('Finish editing to add another ad group'));
  }

  const isEmpty = isLoaded && !loadError && !adGroups.length;

  /*
   * Rendered inside the empty state while there is nothing to list, and in the card's
   * actions once there is — one button either way, so its gating can't drift.
   */
  const addAdGroupButton = (
    <ButtonValid
      disabled={
        disabled || !isLoaded || !!loadError || isBusy || editingAdGroup !== null || undefined
      }
      errors={addAdGroupErrors}
      name={'add-ad-group'}
      onClick={handleAdd}
      tooltipPosition={'top'}
      type={'secondary'}
    >
      {adGroups.length ? t('Add ad group') : t('Create ad group')}
    </ButtonValid>
  );

  return (
    <div className={styles.root} ref={rootRef}>
      <Form label={t('Ads')} width={'wide'}>
        <Card name={'ad-groups'} width={'full'}>
          <Card.Header
            description={t(
              'Ad groups hold the keywords and sitelinks that a set of ads share. Ad Grant policy requires at least {{minAdGroups}} ad groups, each with at least {{minAds}} ads and {{minSitelinks}} sitelinks.',
              {
                minAdGroups: AD_GRANT_LIMITS.minAdGroups,
                minAds: AD_GRANT_LIMITS.minAdsPerAdGroup,
                minSitelinks: AD_GRANT_LIMITS.minSitelinksPerAdGroup,
              },
            )}
            title={t('Ad groups')}
          />
          <Card.Content addVerticalGap={true}>
            {/*
             * The empty state waits on both, so a campaign that has ad groups never
             * flashes "No ad groups yet" while they load — and a failed fetch never
             * claims there are none.
             */}
            {!isLoaded && <Skeleton paragraphs={3} />}
            {loadError && (
              <Alert title={t("Couldn't load this campaign's ad groups.")} type={'danger'}>
                {errorMessage(loadError, t)}
              </Alert>
            )}
            {isEmpty && (
              <EmptyState
                description={t('Add one to start adding keywords, sitelinks, and ads.')}
                label={t('No ad groups yet')}
                name={'ad-groups-empty'}
                // Neutral, because this sits on the card's own white background.
                theme={'neutral'}
                width={'full'}
              >
                {addAdGroupButton}
              </EmptyState>
            )}
            {adGroups.map((adGroup: IAdGroup, index: number) => (
              <AdGroupCard
                adGroup={adGroup}
                adGroupErrors={validation.adGroupErrors[index] ?? []}
                businessName={businessName}
                defaultOpen={index === firstInvalidIndex || index === editingAdGroup}
                disabled={disabled}
                editingAdGroup={editingAdGroup}
                index={index}
                isComplete={
                  !validation.invalidAdGroupIndexes.includes(index) && editingAdGroup !== index
                }
                key={adGroup.key}
                onAddAd={addAd}
                onChange={updateAdGroup}
                onDone={handleDone}
                onEdit={handleEdit}
                onRemoveAd={removeAd}
                onRename={renameAdGroup}
                onRequestDelete={handleRequestDelete}
                onUpdateAd={updateAd}
                rowErrors={validation.rowErrors[index]}
              />
            ))}
          </Card.Content>
          {/* The empty state owns the button while it is showing, so this would double it. */}
          {!isEmpty && <Card.Actions>{addAdGroupButton}</Card.Actions>}
        </Card>
      </Form>
      <ConfirmModal
        alertDescription={t('Its keywords, sitelinks, and ads will be deleted with it.')}
        alertTitle={t('Are you sure you want to delete this ad group?')}
        alertType={'warning'}
        confirmButtonText={t('Delete')}
        confirmButtonType={'danger'}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        opened={pendingDelete !== null}
        t={t}
        title={t('Delete ad group')}
      />
    </div>
  );
}

export default observer(AdStep);
